import { useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

/** The Mantine breakpoint names this hook accepts. */
export type BreakpointName = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Whether the viewport is below a Mantine breakpoint, on the same boundary
 * Mantine's own `hiddenFrom` / `visibleFrom` use.
 *
 * Most responsive work in this app should be a `hiddenFrom` / `visibleFrom`
 * prop or a responsive style object, all of which are pure CSS. This hook is
 * for the cases that genuinely cannot be: `AppShell`'s `footer.collapsed` takes
 * a boolean, and rendering a `Drawer` instead of a fixed rail is a different
 * element rather than a different style.
 *
 * It exists because the query is easy to write slightly differently each time.
 * The `- 0.0625rem` is what makes the boundary line up with Mantine's own
 * media queries instead of overlapping them by a pixel, and
 * `getInitialValueInEffect: false` is what stops the first paint rendering the
 * desktop arrangement on a phone and then correcting itself. Two call sites had
 * already copied both, and `CalendarView` had drifted to a hard-coded 768px.
 */
export function useBelowBreakpoint(breakpoint: BreakpointName): boolean {
  const theme = useMantineTheme();
  return (
    useMediaQuery(`(max-width: calc(${theme.breakpoints[breakpoint]} - 0.0625rem))`, false, {
      getInitialValueInEffect: false,
    }) ?? false
  );
}
